export type ProductTag =
  | "Bestseller"
  | "New"
  | "Vegan"
  | "Signature"
  | "Sculptural"
  | "Wax Melt"
  | "Room Spray"
  | "Made to Order"
  | "Gift Set";

export type ProductType =
  | "jar"
  | "signature"
  | "sculptural"
  | "slate"
  | "bouquet"
  | "spray";

export type ProductCategory = "all" | "new" | "bestseller" | "gift";

export type Product = {
  id: string;
  slug: string;
  name: string;
  subtitle?: string;
  type: ProductType;
  scent: string;
  description: string;
  priceLabel: string;
  priceCents: number | null;
  currency: string;
  tags: ProductTag[];
  categories: ProductCategory[];
  image: string;
  featuredOrder?: number | null;
};

export function priceLabelFor(priceCents: number | null): string {
  if (priceCents == null) return "Made to Order";
  return priceCents % 100 === 0
    ? `$${priceCents / 100}`
    : `$${(priceCents / 100).toFixed(2)}`;
}

// Build-time snapshot of the catalog, baked into the client bundle so
// pre-rendered pages and first paint show products without waiting on the
// API. The live catalog is D1 (`products` table, managed via the Telegram
// bot); the client overlays GET /api/products on top of this as soon as it
// loads. The worker must NEVER read this snapshot for pricing or lookups —
// it uses src/worker/lib/catalog.ts against D1.
export const catalogSnapshot: Product[] = [
  {
    id: "1",
    slug: "tuscan-twilight",
    name: "Tuscan Twilight",
    subtitle: "Tobacco & Vanilla",
    type: "jar",
    scent: "Tobacco · Creamy Vanilla",
    description:
      "The golden hues of a Tuscan sunset — creamy vanilla and smoky tobacco woven into warmth and nostalgia. A scent for those who love a touch of elegance and comfort, transporting you to an evening in the Italian countryside.",
    priceLabel: "$38",
    priceCents: 3800,
    currency: "usd",
    tags: ["Bestseller", "Vegan"],
    categories: ["all", "bestseller"],
    image: "/products/tuscan-twilight.jpg",
    featuredOrder: 1,
  },
  {
    id: "2",
    slug: "harmony-haven",
    name: "Harmony Haven",
    subtitle: "Lavender · Orange · Sandalwood & Vanilla Bean",
    type: "jar",
    scent: "Lavender · Orange · Sandalwood · Vanilla Bean",
    description:
      "A calming invitation into serene tranquility. Lavender and bright orange drift into warm sandalwood and a whisper of creamy vanilla bean — a quiet sanctuary that melts away the stresses of the day.",
    priceLabel: "$38",
    priceCents: 3800,
    currency: "usd",
    tags: ["Bestseller", "Vegan"],
    categories: ["all", "bestseller"],
    image: "/products/harmony-haven.jpg",
  },
  {
    id: "3",
    slug: "cabin-muse",
    name: "Cabin Muse",
    subtitle: "Bergamot · Teakwood · Lavender",
    type: "jar",
    scent: "Bergamot · White Oak · Teakwood · Mahogany · Musk · Patchouli",
    description:
      "Where teakwood meets firelight. A warm, woodsy escape inspired by fall's quiet magic. Light it. Breathe it in. Let solitude speak.",
    priceLabel: "$38",
    priceCents: 3800,
    currency: "usd",
    tags: ["New", "Vegan"],
    categories: ["all", "new"],
    image: "/products/cabin-muse.jpg",
  },
  {
    id: "4",
    slug: "pure-serenity",
    name: "Pure Serenity",
    subtitle: "Rosemary & Mint",
    type: "jar",
    scent: "Rosemary · Cool Mint",
    description:
      "A quiet exhale in candle form. Fresh rosemary unfurls into cool mint, creating a crisp, clarifying harmony that resets the senses. Hand-poured in apricot coconut luxury wax — clean, modern, effortlessly serene.",
    priceLabel: "$38",
    priceCents: 3800,
    currency: "usd",
    tags: ["New", "Vegan"],
    categories: ["all", "new"],
    image: "/products/pure-serenity.jpg",
  },
  {
    id: "5",
    slug: "silk-mirage",
    name: "Silk Mirage",
    subtitle: "BeAdoré Signature Collection",
    type: "signature",
    scent: "Lavender · Vanilla · Patchouli · Amber · Cashmere",
    description:
      "A composition that unfolds like silk — floral calm, warm sweetness, and a lingering trail of ambered cashmere. Poured into BeAdoré's signature faceted lavender vessel, crowned with a gold finial lid and adorned with sculptural petals.",
    priceLabel: "$62",
    priceCents: 6200,
    currency: "usd",
    tags: ["Signature", "Bestseller"],
    categories: ["all", "bestseller"],
    image: "/products/silk-mirage.jpg",
    featuredOrder: 2,
  },
  {
    id: "6",
    slug: "velvet-ember",
    name: "Velvet Ember",
    subtitle: "A Quiet Glow · A Velvet Warmth",
    type: "signature",
    scent: "Peony · Vanilla · Sandalwood · Patchouli",
    description:
      "A gold-rimmed white vessel resting on marble light — soft, modern, impossibly serene. Peony drifts into vanilla, settling into sandalwood and patchouli. A moment of stillness. A breath of luxury. This is BeAdoré.",
    priceLabel: "$62",
    priceCents: 6200,
    currency: "usd",
    tags: ["Signature", "Gift Set"],
    categories: ["all", "gift"],
    image: "/products/velvet-ember.jpg",
  },
  {
    id: "7",
    slug: "tulip-field",
    name: "Tulip Field",
    subtitle: "A Bouquet of Light",
    type: "sculptural",
    scent: "Fresh · Floral · Sun-Kissed",
    description:
      "A candle that feels like a bouquet of light. Each sculpted tulip rises from creamy wax, wrapped in soft mesh and tied with twine — a poetic gesture to spring's quiet elegance. Perfect for gatherings and wedding favours; customisation available on request.",
    priceLabel: "$34",
    priceCents: 3400,
    currency: "usd",
    tags: ["Sculptural", "Gift Set"],
    categories: ["all", "gift"],
    image: "/products/tulip-field.jpg",
    featuredOrder: 3,
  },
  {
    id: "8",
    slug: "halo-aroma-slate",
    name: "Halo",
    subtitle: "Aroma Slate · Lavender & Vanilla",
    type: "slate",
    scent: "Lavender · Vanilla",
    description:
      "A soft veil of lavender and vanilla — serenity sculpted in wax. Pressed florals bloom from a lavender-marbled slate, diffusing a quiet calm through drawers, wardrobes, and the corners of a room.",
    priceLabel: "$22",
    priceCents: 2200,
    currency: "usd",
    tags: ["New", "Wax Melt"],
    categories: ["all", "new"],
    image: "/products/halo-aroma-slate.jpg",
  },
  {
    id: "9",
    slug: "silk-mirage-spray",
    name: "Silk Mirage Room Spray",
    subtitle: "Amber · Vanilla · Lavender",
    type: "spray",
    scent: "Amber · Vanilla · Lavender",
    description:
      "Elevate your space with a luxurious blend of amber, vanilla, and lavender. A room and linen mist that carries the Silk Mirage signature — at 60 mL, 2 oz.",
    priceLabel: "$28",
    priceCents: 2800,
    currency: "usd",
    tags: ["New", "Room Spray"],
    categories: ["all", "new"],
    image: "/products/silk-mirage-spray.jpg",
  },
  {
    id: "10",
    slug: "bloomscape",
    name: "Bloomscape",
    subtitle: "The Grand Floral Wax Bouquet",
    type: "bouquet",
    scent: "Magnolia · Peony · Tulip Field · Rose Charm",
    description:
      "A sculptural ode to craftsmanship and scent — BeAdoré's grand floral wax bouquet. Hand-poured in coco-soy wax, shaped with precision, radiating elegance and light. Custom or seasonal; please order two weeks prior to any event. Choose up to two colours and your preferred scent blend.",
    priceLabel: "Made to Order",
    priceCents: null,
    currency: "usd",
    tags: ["Made to Order", "Signature"],
    categories: ["all", "gift"],
    image: "/products/bloomscape.jpg",
  },
];
